import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import DataTableWrapper from "../../../Components/CommonTableLayout/DataTableWrapper";
import { Link } from "react-router-dom";
import { fetchInventory, fetchInventoryTotalStock, fetchOnhand, fetchFilterOptions } from "../../../services/Inventory/InventoryServices";
import StockBar from "../../../Components/DetailsPageUpperLayer/StockBar";
import { getProjectionDotColor } from "../../../utils/colorUtils";
import { formatValue } from "../../../utils/currencyUtils";
import {
  getInitialFilterModel,
  createColumnFilterChangeHandler,
  createCascadingFilterParams,
  createDateFilterParams
} from "../../../utils/columnFilterUtils";
import OnHandModal from "../../../Components/DetailsPageUpperLayer/OnHandModal";
import TotalStockModal from "../../../Components/DetailsPageUpperLayer/TotalStockModal";
import { Box, Typography, Divider } from "@mui/material";
import "../../../CommonComponents/common/datatable/DataTable.css";

const DEFAULT_PAGE_SIZE = 25;
const FILTER_CACHE_KEY = 'inventory';

function mapApiInventoryRows(apiRows = []) {
  return apiRows.map((row) => {
    const safe = (v) => v !== undefined && v !== null && v !== "" ? Number(v) : null;

    return {
      inventoryId: row.inventoryId,
      item: `${row.item}/${row.siteCode}`,
      itemName: row.itemName,
      materialGroup: row.materialGroup,
      materialGroupDesc: row.materialGroupDesc,
      mrpController: row.mrpController,
      materialType: row.materialType,
      siteCode: row.siteCode,
      siteName: row.siteName,
      stock: safe(row.stock?.current),
      min_stock: safe(row.stock?.min),
      max_stock: safe(row.stock?.max),
      projected_stock: safe(row.stock?.projected),
      projection6W: [
        row.projection6W?.week1 ?? null,
        row.projection6W?.week2 ?? null,
        row.projection6W?.week3 ?? null,
        row.projection6W?.week4 ?? null,
        row.projection6W?.week5 ?? null,
        row.projection6W?.week6 ?? null,
      ],
      projected_stock_per_week: [
        safe(row.projection6W?.week1),
        safe(row.projection6W?.week2),
        safe(row.projection6W?.week3),
        safe(row.projection6W?.week4),
        safe(row.projection6W?.week5),
        safe(row.projection6W?.week6),
      ],
      min_stock_6W: [
        safe(row.minStock6W?.week1),
        safe(row.minStock6W?.week2),
        safe(row.minStock6W?.week3),
        safe(row.minStock6W?.week4),
        safe(row.minStock6W?.week5),
        safe(row.minStock6W?.week6),
      ],
      max_stock_6W: [
        safe(row.maxStock6W?.week1),
        safe(row.maxStock6W?.week2),
        safe(row.maxStock6W?.week3),
        safe(row.maxStock6W?.week4),
        safe(row.maxStock6W?.week5),
        safe(row.maxStock6W?.week6),
      ],
      occurrenceDate: row.occurrenceDate,
      totalStock: formatValue(row.totalStock),
      onHand: formatValue(row.onHand),
      monetaryImpact: row.monetaryImpact,
      exceptionType: row.exceptionType,
    };
  });
}

const InventoryTable = ({ filters, setRowCount, onColumnFilterChanged }) => {
  // Strict cascading filter mapping (filterType + values)
  const mapFiltersForApi = useCallback((columnFilters) => {
    const mapped = { appliedFilters: {} };
    Object.entries(columnFilters || {}).forEach(([key, filterObj]) => {
      if (key !== 'state' && key !== 'exceptionType') {
        if (filterObj && typeof filterObj === 'object' && 'filterType' in filterObj && 'values' in filterObj) {
          mapped.appliedFilters[key] = {
            filterType: filterObj.filterType,
            values: filterObj.values
          };
        } else {
          let filterType = undefined;
          if (Array.isArray(filterObj)) {
            filterType = 'set';
          } else if (filterObj && typeof filterObj === 'object' && filterObj.type) {
            const dateTypes = new Set(['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank']);
            filterType = dateTypes.has(filterObj.type) ? 'date' : 'text';
          } else {
            filterType = 'unknown';
          }
          mapped.appliedFilters[key] = {
            filterType,
            values: filterObj
          };
        }
      }
    });
    if (filters?.states && filters.states.length > 0) {
      mapped.selectedStates = filters.states.map(s => s.detailName || s);
    }
    if (filters?.exceptions && filters.exceptions.length > 0) {
      mapped.selectedExceptions = filters.exceptions.map(e => e.detailName || e);
    }
    return mapped;
  }, [filters]);

  const [currentFilters, setCurrentFilters] = useState({});
  const [openTotalStockModal, setOpenTotalStockModal] = useState(false);
  const [openOnhandModal, setOpenonHandModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [loadingModalData, setLoadingModalData] = useState(false);
  const fetchCacheRef = useRef(new Map());
  const debounceTimersRef = useRef({});


  const initialFilterModel = useMemo(() => getInitialFilterModel(FILTER_CACHE_KEY), []);

  const fetchOptionsStable = useCallback((col, columnFilters) => {
      const apiPayload = mapFiltersForApi(columnFilters);
      const key = `${col}::${JSON.stringify(apiPayload)}`;
      if (fetchCacheRef.current.has(key)) {
        return fetchCacheRef.current.get(key);
      }
  
      let resolveFn, rejectFn;
      const p = new Promise((res, rej) => { resolveFn = res; rejectFn = rej; });
      fetchCacheRef.current.set(key, p);
  
      if (debounceTimersRef.current[key]) clearTimeout(debounceTimersRef.current[key]);
      debounceTimersRef.current[key] = setTimeout(async () => {
        try {
          const res = await fetchFilterOptions(col, apiPayload);
          resolveFn(res);
        } catch (err) {
          fetchCacheRef.current.delete(key);
          rejectFn(err);
        } finally {
          delete debounceTimersRef.current[key];
        }
      }, 120);
  
      return p;
    }, [mapFiltersForApi]);


 const handleColumnFilterChanged = useCallback((columnFilters, rawFilterModel) => {
    try {
      // Save enriched AG Grid model for UI restoration
      if (rawFilterModel) {
        const enrichedRaw = Object.fromEntries(
          Object.entries(rawFilterModel || {}).map(([field, model]) => {
            let filterType = model?.filterType;
            if (!filterType) {
              if (Array.isArray(model?.values)) {
                filterType = 'set';
              } else if (model?.type) {
                const dateTypes = new Set(['equals','notEqual','lessThan','greaterThan','inRange','blank','notBlank']);
                filterType = dateTypes.has(model.type) ? 'date' : 'text';
              } else {
                filterType = 'unknown';
              }
            }
            return [field, { ...model, filterType }];
          })
        );
        localStorage.setItem(FILTER_CACHE_KEY, JSON.stringify(enrichedRaw));
      }
    } catch (error) {
      console.error('Error saving filters:', error);
    }

    setCurrentFilters(columnFilters);

    if (typeof onColumnFilterChanged === 'function') {
      onColumnFilterChanged(columnFilters);
    }

    try {
      const pageKey = FILTER_CACHE_KEY; 
      const simplifiedWithTypes = Object.fromEntries(
        Object.entries(columnFilters || {}).map(([field, value]) => {
          let filterType = undefined;
          const raw = rawFilterModel?.[field];
          if (raw?.filterType) {
            filterType = raw.filterType;
          } else if (Array.isArray(raw?.values)) {
            filterType = 'set';
          } else if (raw?.type) {
            const dateTypes = new Set(['equals','notEqual','lessThan','greaterThan','inRange','blank','notBlank']);
            filterType = dateTypes.has(raw.type) ? 'date' : 'text';
          }
          return [field, { filterType, values: value }];
        })
      );
      localStorage.setItem(`columnFilters_${pageKey}`, JSON.stringify(simplifiedWithTypes));
      window.dispatchEvent(new CustomEvent(`columnFiltersChanged_${pageKey}`));
    } catch (err) {
      console.warn('Failed to persist/notify column filter change', err);
    }
  }, [onColumnFilterChanged]);



  const handleTotalStockClick = async (item, siteCode) => {
    setModalData([]);
    setOpenTotalStockModal(true);
    setLoadingModalData(true);
    try {
      const response = await fetchInventoryTotalStock(item, siteCode);
      const data = Array.isArray(response) ? response : [response];
      setModalData(data);
    } catch (err) {
      setModalData([]);
    } finally {
      setLoadingModalData(false);
    }
  };


  const handleOnhandClick = async (item, siteCode) => {
    setModalData([]);
    setOpenonHandModal(true);
    setLoadingModalData(true);
    try {
      const response = await fetchOnhand(item, siteCode);
      const data = Array.isArray(response) ? response : [response];
      setModalData(data);
    } catch (err) {
      setModalData([]);
    } finally {
      setLoadingModalData(false);
    }
  };


  const totalStockColumn = useMemo(
    () => [
      { field: "time", headerName: "Time" },
      { field: "quantity", headerName: "Quantity" },
      { field: "stockCategory", headerName: "Batch Status" },
      { field: "batch", headerName: "Batch" },
      { field: "exception", headerName: "Exception" },
      { field: "expiry", headerName: "Best Before Date" },
      { field: "storageLocation", headerName: "Storage Location" },
    ],
    []
  );
  // On Hand modal columns
  const onHandColumn = useMemo(
    () => [
      { field: "time", headerName: "Time" },
      { field: "quantity", headerName: "Quantity" },
      { field: "stockCategory", headerName: "Batch Status" },
      { field: "batch", headerName: "Batch" },
      { field: "exception", headerName: "Exception" },
      { field: "expiry", headerName: "Best Before Date" },
      { field: "storageLocation", headerName: "Storage Location" },
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        field: "item",
        headerName: "Material Number",
        filter: 'agSetColumnFilter',
        filterParams: createCascadingFilterParams(
          (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
          'item',
          currentFilters
        ),
        cellRenderer: (params) => {
          const displayValue = params.value?.split("/")[0] || "";
          let borderColor = "transparent";
          switch (params.data?.exceptionType) {
            case "Stockout":
              borderColor = "#e41c24";
              break;
            case "Batch Expired":
              borderColor = "#ff560e";
              break;
            case "Overstock":
              borderColor = "#bc6bd9";
              break;
            case "Low Stock":
              borderColor = "#f79900";
              break;
            case "Batch Expiring":
              borderColor = "#e5813e";
              break;
            case "On Track":
              borderColor = "#268000";
              break;
            case "No Exception":
              borderColor = "#000";
              break;
            default:
              borderColor = "black";
          }
          return (
            <div
              style={{
                borderLeft: `5px solid ${borderColor}`,
                paddingLeft: 8,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Link
                to={`inventory-detail/${params.value}`}
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {displayValue}
              </Link>
            </div>
          );
        },
      },
      { field: "itemName", headerName: "Material Description",filter: 'agSetColumnFilter',
        filterParams: createCascadingFilterParams(
          (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
          'itemName',
          currentFilters
        ) },
      {
        field: "materialGroup",
        headerName: "Material Group",
        filter: 'agSetColumnFilter',
        filterParams: createCascadingFilterParams(
          (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
          'materialGroup',
          currentFilters
        )
      },
      {
        field: "materialGroupDesc",
        headerName: "Material Group Description",
        filter: 'agSetColumnFilter',
         filterParams: createCascadingFilterParams(
          (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
          'materialGroupDesc',
          currentFilters
        )
      },
      {
        field: "mrpController",
        headerName: "MRP Controller",
        filter: 'agSetColumnFilter',
        filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'mrpController', currentFilters)
      },
      {
        field: "materialType", headerName: "Material Type", filter: 'agSetColumnFilter',
        filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'materialType', currentFilters)
      },
      {
        field: "siteCode",
        headerName: "Plant Number",
        filter: 'agSetColumnFilter',
        filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'siteCode', currentFilters)
      },
      {
        field: "siteName",
        headerName: "Plant Name",
        filter: 'agSetColumnFilter',
        filterParams: createCascadingFilterParams((col, filtersForCol) => fetchOptionsStable(col, filtersForCol), 'siteName', currentFilters)
      },
      {
        field: "stock",
        headerName: "Unrestricted Stock",
        minWidth: 250,
        flex: 2,
        cellRenderer: (params) => (
          <StockBar
            stock={params.data.stock}
            min_stock={params.data.min_stock}
            max_stock={params.data.max_stock}
            projected_stock={params.data.projected_stock}
            source="list"
          />
        ),
      },
      {
        field: "projection6W",
        headerName: "Projection",
        sortable: false,
        cellRenderer: (params) => {
          const projected = params.data.projected_stock_per_week || [0, 0, 0, 0, 0, 0];
          const min_stock = params.data.min_stock_6W || [0, 0, 0, 0, 0, 0];
          const max_stock = params.data.max_stock_6W || [0, 0, 0, 0, 0, 0];
          // column to show 6 dots representing each week's projection  

          return (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {projected.map((value, idx) => {
                const color = getProjectionDotColor(Number(value), min_stock[idx], max_stock[idx]);
                return (
                  <div
                    key={idx}
                    title={`Week ${idx + 1}: ${formatValue(value)}`}
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: color,
                      border: "1px solid #ccc",
                      display: "inline-block",
                      marginTop: "12px"
                    }}
                  />
                );
              })}
            </div>
          );
        },
      },
      {
        field: "occurrenceDate",
        headerName: "Occurrence Date",
        filter: 'agDateColumnFilter',
        filterParams: createDateFilterParams()
      },
      {
        field: "onHand",
        headerName: "On Hand",
        cellRenderer: (params) => {
          const [item, siteCode] = params.data.item.split("/");
          return (
            <span
              style={{
                color: "#555",
                fontSize: "0.9rem",
                fontWeight: "bold",
                textDecoration: "none",
                cursor: "pointer",
              }}
              onClick={() => handleOnhandClick(item, params.siteCode || siteCode)}
            >
              {formatValue(params.data.onHand)}
            </span>
          );
        },
      },
      {
        field: "totalStock",
        headerName: "Total Stock",
        cellRenderer: (params) => {
          const [item, siteCode] = params.data.item.split("/");
          return (
            <span
              style={{
                color: "#555",
                fontSize: "0.9rem",
                fontWeight: "bold",
                textDecoration: "none",
                cursor: "pointer",
              }}
              onClick={() => handleTotalStockClick(item, params.siteCode || siteCode)}
            >
              {formatValue(params.data.totalStock)}
            </span>
          );
        },
      },
      { field: "monetaryImpact", headerName: "Monetary Impact" },
    ],
    [currentFilters,fetchOptionsStable]
  );

  const fetchRows = useCallback(
    async ({ page, size, sort }) => {
      try {
        let mappedColumnFilters = null;
        if (filters?.columnFilters) {
          const filtered = Object.keys(filters.columnFilters).reduce((acc, key) => {
            if (columns.some(col => col.field === key)) {
              acc[key] = filters.columnFilters[key];
            }
            return acc;
          }, {});
          mappedColumnFilters = mapFiltersForApi(filtered).appliedFilters;
        }
        const validatedFilters = {
          ...filters,
          columnFilters: mappedColumnFilters
        };

        const response = await fetchInventory({
          page,
          size,
          filters: validatedFilters,
          sort,
        });

        const rows = mapApiInventoryRows(response.content || []);
        const totalElements = response.totalElements || 0;

        if (setRowCount) setRowCount(totalElements);
        return { rows, totalElements };
      } catch {
        setRowCount(0);
        return { rows: [], totalElements: 0 };
      }
    },
    [filters, setRowCount, columns]
  );
 

  return (
    <Box
      sx={{
        height: "calc(100vh - 250px)",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
        pb: 1 
      }}
    >
      <DataTableWrapper
        title="Inventory"
        columns={columns}
        filters={filters}
        height={500}
        fetchRows={fetchRows}
        rowCount={undefined}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        onColumnFilterChanged={handleColumnFilterChanged}
        initialFilterModel={initialFilterModel}
      />

      <OnHandModal
        open={openOnhandModal}
        handleClose={() => setOpenonHandModal(false)}
        rows={modalData}
        columns={onHandColumn}
        loading={loadingModalData}
      />
      <TotalStockModal
        open={openTotalStockModal}
        handleClose={() => setOpenTotalStockModal(false)}
        rows={modalData}
        columns={totalStockColumn}
        loading={loadingModalData}
      />
    </Box>
  );
};

export default InventoryTable;